<script lang="ts" setup>
  import { provide, ref } from "vue";
  import { useSubscriptionFlowStore } from "@/stores/subscriptionFlow";
  import { useAppStore } from "@/stores/app";
  import { useSessionStore } from "@/stores/session";
  import * as amplitude from "@amplitude/analytics-browser";
  import MembershipTierListWithTitle from "@/components/membership/MembershipTierListWithTitle.vue";
  import SimpleModal from "@/components/modals/SimpleModal.vue";
  import { MembershipTierPlanFrequency } from "@/models/membershipTierPlan";

  const appStore = useAppStore();
  const subscriptionFlowStore = useSubscriptionFlowStore();
  const sessionStore = useSessionStore();

  const showUpsellModal = ref<boolean>(false);
  const upsellName = ref<string>('');
  const upsellPrice = ref<number>(0);
  const upsellSavings = ref<number>(0);

  // Provide function to call on membership tier selection
  provide('selectMembershipTier', (tierId: number, tierPlanId?: number) => {
    subscriptionFlowStore.membershipTierId = tierId;
    const chosenTier = appStore.membershipTiers.find((e) => e.id === tierId);
    if (tierPlanId) {
      subscriptionFlowStore.membershipTierPlanId = tierPlanId;

      const chosenTierPlan = chosenTier?.membershipTierPlans.find((e) => e.id === tierPlanId);

      if ((chosenTierPlan?.price ?? 0) > (sessionStore.currentTierPlan?.price ?? 0) && chosenTierPlan?.frequency === MembershipTierPlanFrequency.MONTH) {
        const annualTierPlan = chosenTier?.membershipTierPlans.find((e) => e.frequency === MembershipTierPlanFrequency.YEAR);
        // Upsell modal
        upsellName.value = chosenTier?.name ?? '';
        upsellPrice.value = annualTierPlan?.price ?? 0;
        upsellSavings.value = (chosenTierPlan?.price ?? 0) * 12 - (annualTierPlan?.price ?? 0);
        showUpsellModal.value = true;
        amplitude.track('Upsell Modal Displayed', {
          "Tier Name": chosenTier?.name ?? '',
          "Tier Cycle": chosenTierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
          "Tier Price": (chosenTierPlan?.price ?? 0) < 0 ? 'Roundup' : (chosenTierPlan?.price ?? 0),
        });
        return;
      }
    }

    advanceStep();
  });

  function advanceStep() {
    const tier = appStore.membershipTiers.find((e) => e.id === subscriptionFlowStore.membershipTierId);
    const tierPlan = tier?.membershipTierPlans.find((e) => e.id === subscriptionFlowStore.membershipTierPlanId);

    amplitude.track('Membership Selected', {
      "Tier Name": tier?.name ?? '',
      "Tier Cycle": tierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
      "Tier Price": (tierPlan?.price ?? 0) < 0 ? 'Roundup' : tierPlan?.price ?? '',
    });
    subscriptionFlowStore.advanceStep();
  }

  function handleSwitchAnnualClick() {
    showUpsellModal.value = false;
    const tier = appStore.membershipTiers.find((e) => e.id === subscriptionFlowStore.membershipTierId);
    const annualPlan = tier?.membershipTierPlans.find((tp) => tp.frequency === MembershipTierPlanFrequency.YEAR);

    subscriptionFlowStore.membershipTierPlanId = annualPlan?.id ?? null;

    trackUpsellClick(tier, annualPlan, 'Switch to annual');
    advanceStep();
  }

  function handleStayMonthlyClick() {
    showUpsellModal.value = false;

    const chosenTier = appStore.membershipTiers.find((e) => e.id === subscriptionFlowStore.membershipTierId);
    const chosenTierPlan = chosenTier?.membershipTierPlans.find((e) => e.id === subscriptionFlowStore.membershipTierPlanId);

    trackUpsellClick(chosenTier, chosenTierPlan, 'Stay with monthly');
    advanceStep();
  }

  function trackUpsellClick(tier: any, tierPlan: any, action: string) {
    amplitude.track('Upsell Button Clicked', {
      "Tier Name": tier?.name ?? '',
      "Tier Cycle": tierPlan?.frequency ?? MembershipTierPlanFrequency.MONTH,
      "Tier Price": tier?.price < 0 ? 'Roundup' : tierPlan.price,
      "Action": action
    });
  }
</script>

<template>
  <MembershipTierListWithTitle
    :title="$t('subscription.flow.tier.title.upgrade')"
  />

  <SimpleModal
    :model-value="showUpsellModal"
    @update:model-value="handleStayMonthlyClick"
    width="500"
  >
    <div class="upsell-modal">
      <div class="t-title-2 t-bold">
        {{ $t('subscription.flow.tier.upsell.title', { value: upsellSavings }) }}
      </div>
      <div class="t-body-1">
        {{ $t('subscription.flow.tier.upsell.description', { price: upsellPrice, name: upsellName }) }}
      </div>
      <div class="actions">
        <v-btn class="full" @click="handleSwitchAnnualClick">
          {{ $t('subscription.flow.tier.upsell.btn.switch') }}
        </v-btn>
        <v-btn class="full" variant="outlined" @click="handleStayMonthlyClick">
          {{ $t('subscription.flow.tier.upsell.btn.stay') }}
        </v-btn>
      </div>
    </div>
  </SimpleModal>
</template>

<style lang="scss" scoped>
  .upsell-modal {
    display: flex;
    flex-direction: column;
    align-items: start;
    text-align: center;
    gap: 2rem;

    .actions {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .full {
      width: 100%;
      border-radius: 0.5rem;
    }
  }
</style>